/**
 * CaricaDalTelefono.jsx — «Scatta» e «Carica» in fondo alla cartella del
 * paziente, per il medico al telefono (proposta telefono-stile-nuovo, punto 1,
 * con le decisioni di Alex del 28/10/2026, riferite). Solo nello stile nuovo,
 * solo fino a 480 px, solo per chi e' collegato a un medico: lo decide chi
 * monta questo componente (navigazione/telefono.js, vistaTelefonoDelMedico).
 *
 * COME FUNZIONA
 * - «Scatta» apre la fotocamera DENTRO la pagina (getUserMedia, la posteriore):
 *   la foto clinica non passa dalla galleria del telefono. Piu' scatti di
 *   fila, con la miniatura di ognuno e «Togli» sull'ultimo. Dove la
 *   fotocamera nella pagina non c'e' (browser vecchio, permesso negato) si
 *   apre quella del telefono (input con capture), e il manuale dice cosa fare
 *   della copia che resta nella galleria.
 * - «Carica» apre il selettore dei file (immagini e PDF, i formati che il
 *   server riconosce dai byte).
 * - Alla fine si sceglie: FOTOGRAFIE (una per scatto, nella galleria del
 *   paziente: la rotta delle fotografie di sempre, con l'etichetta) o un
 *   DOCUMENTO (gli scatti, nell'ordine, diventano UN PDF composto SUL SERVER
 *   dalla rotta dei referti di sempre, campo «pagine»; un PDF scelto dal
 *   telefono parte com'e'). Nessuna rotta nuova per il telefono.
 * - Prima di partire la foto si riduce qui: 3000 px sul lato lungo per le
 *   fotografie, 2400 per le pagine di un documento, JPEG 0,85
 *   (utils/riduciImmagine.js).
 * - L'avanzamento si vede file per file; se la rete cade si riprova da soli
 *   fino a tre volte (utils/invioConTentativi.js), poi il file resta in rosso
 *   con «Riprova». Mai un file perso in silenzio: finche' c'e' qualcosa di non
 *   inviato la pagina e' «sporca» (useUscitaProtetta), quindi non si ricarica
 *   da sola (la terza difesa) e chiudere chiede conferma.
 * - Gli scatti stanno SOLO in memoria (Blob e URL di anteprima, revocati alla
 *   chiusura): mai in localStorage, mai nella cache.
 *
 * CON LA BARRA IN BASSO (punto 4, variante B; Alex 28/10/2026, riferita)
 * «Scatta» sta al centro della barra del telefono (components/guscio/Telefono.jsx):
 *   - `barra`: 'completa' (Scatta e Carica in fondo, il primo pezzo), 'carica'
 *     (solo Carica: Scatta e' nella barra) o 'nessuna';
 *   - `ascoltaScatta`: la scheda aperta registra il suo «Scatta», cosi' la
 *     barra scatta per QUESTO paziente (navigazione/telefono.js,
 *     registraScatto);
 *   - `avvio="scatta"`: montato da «Scatta» fuori da una scheda, dopo la scelta
 *     del paziente, si apre subito sulla fotocamera; `perChi` scrive il nome in
 *     testa al pannello (la scelta e' appena fatta, ma chi scatta deve vederla);
 *     `onChiuso` lo dice a chi l'ha montato.
 *
 * DOVE VANNO: LA VISITA DI OGGI DEL MEDICO (29/10/2026, classe A: «applica la
 * regola di riservatezza che c'e'»)
 *   - con la scheda aperta su una visita di oggi del medico stesso
 *     (`visitaAperta`, la visita dell'indirizzo), «Scatta» e «Carica» vanno su
 *     QUELLA visita: le fotografie con la strada del computer (la rotta delle
 *     fotografie con visita_id, come ModuloFoto), i documenti fra i documenti
 *     della procedura (la stessa rotta, «uso: documento», come
 *     DocumentiProcedura; le pagine fotografate diventano un PDF sul server).
 *     La riservatezza la applica il server, come per il computer;
 *   - fuori dalla visita, se il paziente ha OGGI una visita RISERVATA dello
 *     stesso medico, prima della galleria si propone quella visita (un
 *     tasto); la galleria resta, col suo avviso;
 *   - «di oggi» e «del medico» li dice il server (`di_oggi` e `del_medico`
 *     nelle visite del paziente, la rotta di sempre): qui non si calcola ne'
 *     il giorno ne' il medico. Le visite si rileggono a ogni apertura del
 *     pannello, cosi' una visita nata un minuto fa c'e'.
 *   La scelta e' destinazioneDa(), qui sotto: una funzione pura, provata in
 *   jsdom (navigazione/telefonoDestinazione.test.js).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { accettaDi } from '../../utils/tipiFile';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import { annunciaFotoCambiate } from '../../utils/annunci';
import { messaggioDa } from '../../utils/notifiche';
import { riduci, eHeic, MSG_HEIC_NON_APRIBILE, LATO_FOTO, LATO_DOCUMENTO } from '../../utils/riduciImmagine';
import { conTentativi, TENTATIVI, nuovoInvioId, INTESTAZIONE_INVIO } from '../../utils/invioConTentativi';
import { ETICHETTE } from './FotoTab';
import { CATEGORIE } from './EsamiRefertiTab';
import { registraScatto } from '../../navigazione/telefono';
import '../../styles/CaricaDalTelefonoConsole.css';

/* I formati che il server riconosce (utils/tipiFile.js, copiato da
   backend/utils/tipoFile.js): mai «image/*», che offre anche l'SVG. */
const ACCETTA_CARICA = accettaDi(['image/', 'application/pdf']);
const ACCETTA_SCATTO = accettaDi(['image/']);

/* Solo per la schermata (che cosa si puo' scegliere): a decidere sono i byte,
   sul server. */
const ePdf = (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');
const comeJpeg = (nome) => `${String(nome || 'foto').replace(/\.[^.]*$/, '')}.jpg`;

/* Le parole della destinazione: le citano il manuale dei medici e le prove. */
export const MSG_NELLA_VISITA = 'Vanno nella visita di oggi.';
export const MSG_NELLA_VISITA_RISERVATA = 'Vanno nella visita riservata di oggi.';
export const MSG_AVVISO_GALLERIA = 'Nella galleria fotografie e documenti li vedono tutti i medici della struttura: '
    + 'non seguono la riservatezza della visita.';
export const MSG_VISITE_NON_LETTE = 'Non riesco a sapere se c\'è una visita di oggi.';

/**
 * Dove vanno gli scatti, dalle visite del paziente come le manda il server.
 * @param {object[]|null} visite  le visite (con di_oggi e del_medico)
 * @param {number|null} visitaAperta  la visita aperta nella scheda, se c'e'
 * @returns {{tipo: 'visita'|'proposta'|'galleria', visita: object|null}}
 *   'visita': la visita aperta, di oggi e del medico: si va li' e basta;
 *   'proposta': fuori dalla visita, una riservata di oggi del medico;
 *   'galleria': nessuna delle due, come prima.
 */
export function destinazioneDa(visite, visitaAperta = null) {
    const mie = (Array.isArray(visite) ? visite : [])
        .filter((v) => v && v.di_oggi === true && v.del_medico === true && !v.cancellato_il);
    const aperta = visitaAperta ? mie.find((v) => Number(v.id) === Number(visitaAperta)) : null;
    if (aperta) return { tipo: 'visita', visita: aperta };
    /* Due riservate lo stesso giorno: la piu' recente, che e' quella su cui
       si sta lavorando. */
    const riservate = mie.filter((v) => v.riservata === true).sort((a, b) => Number(b.id) - Number(a.id));
    if (riservate.length) return { tipo: 'proposta', visita: riservate[0] };
    return { tipo: 'galleria', visita: null };
}

export const MSG_PAGINA_NON_APRIBILE = (n) =>
    `La pagina ${n} non si apre in questo browser: per un documento servono fotografie JPEG o PNG.`;

const STATO_TESTO = { attesa: 'in coda', fatto: 'caricato' };

const CaricaDalTelefono = ({ pazienteId, visitaAperta = null, barra = 'completa', ascoltaScatta = false, avvio = null, perChi = '', onChiuso = null }) => {
    const [aperto, setAperto] = useState(false);
    const [fase, setFase] = useState('elenco');          // fotocamera | elenco | invio
    const [camera, setCamera] = useState('spenta');      // spenta | avvio | attiva | non_disponibile
    const [elementi, setElementi] = useState([]);        // { id, blob, nome, pdf, anteprima }
    const [invii, setInvii] = useState([]);              // { id, titolo, stato, progresso, tentativo, messaggio }
    const [etichetta, setEtichetta] = useState(ETICHETTE[0]);
    const [categoria, setCategoria] = useState('altro');
    const [descrizione, setDescrizione] = useState('');
    /* La destinazione: si rilegge a ogni apertura del pannello (e con
       «Riprova»). `galleria` e' la scelta della galleria al posto della
       visita proposta. */
    const [dest, setDest] = useState({ stato: 'leggo', tipo: 'galleria', visita: null });
    const [lettura, setLettura] = useState(0);
    const [galleria, setGalleria] = useState(false);

    const prossimo = useRef(0);
    const compiti = useRef(new Map());                    // id -> { prepara, manda, foto, pronto }
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    /* Il numero della richiesta della fotocamera in corso: una risposta di
       getUserMedia che arriva dopo «Fine», dopo la chiusura o dopo che la
       scheda se n'e' andata non e' piu' di nessuno, e si spegne subito.
       Senza, il permesso dato in ritardo lasciava la fotocamera accesa
       (la spia del telefono) su una pagina che non la mostrava piu'
       (revisione del 29/10). */
    const richiestaRef = useRef(0);
    const caricaRef = useRef(null);
    const ripiegoRef = useRef(null);
    const elementiOra = useRef([]);
    elementiOra.current = elementi;

    /* Non inviato = scelto e non ancora mandato, o mandato e non arrivato. */
    const sporco = elementi.length > 0 || invii.some((i) => i.stato !== 'fatto');
    const chiedi = useUscitaProtetta(sporco);

    const fermaFotocamera = useCallback(() => {
        richiestaRef.current += 1;
        const s = streamRef.current;
        if (s) s.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    /* Alla fine: fotocamera spenta e anteprime revocate (gli scatti non
       restano in memoria oltre la scheda). */
    useEffect(() => () => {
        fermaFotocamera();
        elementiOra.current.forEach((e) => e.anteprima && URL.revokeObjectURL(e.anteprima));
    }, [fermaFotocamera]);

    useEffect(() => {
        const v = videoRef.current;
        if (camera !== 'attiva' || fase !== 'fotocamera' || !v || !streamRef.current) return;
        v.srcObject = streamRef.current;
        const p = v.play && v.play();
        if (p && p.catch) p.catch(() => {});
    }, [camera, fase]);

    useEffect(() => {
        if (!aperto || !pazienteId) return undefined;
        let vivo = true;
        setDest((d) => ({ ...d, stato: 'leggo' }));
        api.get(`/visite/paziente/${pazienteId}`)
            .then((r) => {
                if (!vivo) return;
                const visite = r?.data?.data?.visite;
                if (!r?.data?.success || !Array.isArray(visite)) { setDest({ stato: 'errore', tipo: 'galleria', visita: null }); return; }
                setDest({ stato: 'fatto', ...destinazioneDa(visite, visitaAperta) });
            })
            .catch(() => { if (vivo) setDest({ stato: 'errore', tipo: 'galleria', visita: null }); });
        return () => { vivo = false; };
    }, [aperto, pazienteId, visitaAperta, lettura]);

    const aggiungi = (lista) => {
        const nuovi = lista.map((x) => {
            prossimo.current += 1;
            return { id: prossimo.current, blob: x.blob, nome: x.nome, pdf: x.pdf,
                anteprima: x.pdf ? null : URL.createObjectURL(x.blob) };
        });
        setElementi((prima) => [...prima, ...nuovi]);
    };

    const apriFotocamera = () => {
        const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
        if (!md || typeof md.getUserMedia !== 'function') {
            /* Nello stesso gesto: un clic su un selettore dopo un'attesa il
               browser lo puo' rifiutare. */
            if (ripiegoRef.current) ripiegoRef.current.click();
            /* Aperto da «Scatta» fuori dalla scheda il pannello si vede gia':
               se il browser ha rifiutato il clic, il pulsante resta li'. */
            if (avvio === 'scatta') { setAperto(true); setFase('fotocamera'); setCamera('non_disponibile'); }
            return;
        }
        setAperto(true);
        setFase('fotocamera');
        setCamera('avvio');
        richiestaRef.current += 1;
        const mia = richiestaRef.current;
        md.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
            audio: false,
        }).then((stream) => {
            if (mia !== richiestaRef.current) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            streamRef.current = stream;
            setCamera('attiva');
        }).catch(() => setCamera('non_disponibile'));
    };

    const scatta = () => {
        const v = videoRef.current;
        if (!v || !v.videoWidth) return;
        const tela = document.createElement('canvas');
        tela.width = v.videoWidth;
        tela.height = v.videoHeight;
        tela.getContext('2d').drawImage(v, 0, 0);
        tela.toBlob((b) => {
            tela.width = 0; tela.height = 0;
            if (b) aggiungi([{ blob: b, nome: `scatto_${prossimo.current + 1}.jpg`, pdf: false }]);
        }, 'image/jpeg', 0.92);
    };

    const fineScatti = () => {
        fermaFotocamera();
        setCamera('spenta');
        setFase('elenco');
    };

    const dalSelettore = (ev) => {
        const files = Array.from(ev.target.files || []);
        ev.target.value = '';
        if (!files.length) return;
        aggiungi(files.map((f) => ({ blob: f, nome: f.name, pdf: ePdf(f) })));
        fermaFotocamera();
        setCamera('spenta');
        setAperto(true);
        setFase('elenco');
    };

    const togliUltimo = () => {
        setElementi((prima) => {
            const ultimo = prima[prima.length - 1];
            if (ultimo && ultimo.anteprima) URL.revokeObjectURL(ultimo.anteprima);
            return prima.slice(0, -1);
        });
    };

    const azzera = () => {
        fermaFotocamera();
        setCamera('spenta');
        elementi.forEach((e) => e.anteprima && URL.revokeObjectURL(e.anteprima));
        setElementi([]);
        setInvii([]);
        compiti.current.clear();
        setDescrizione('');
        setGalleria(false);
        setFase('elenco');
        setAperto(false);
    };

    const chiudi = () => {
        if (!chiedi()) return;
        azzera();
        if (onChiuso) onChiuso();
    };

    /* Il «Scatta» della barra in basso, per il paziente di questa scheda.
       L'ultimo gesto si legge da un riferimento: la registrazione resta la
       stessa per tutta la vita della scheda. */
    const gestoScatta = useRef(null);
    gestoScatta.current = apriFotocamera;
    useEffect(() => {
        if (!ascoltaScatta) return undefined;
        return registraScatto(() => gestoScatta.current());
    }, [ascoltaScatta]);

    /* Montato da «Scatta» fuori dalla scheda: la fotocamera subito. */
    useEffect(() => {
        if (avvio === 'scatta') gestoScatta.current();
    }, [avvio]);

    const aggiorna = (id, cambi) => setInvii((prima) => prima.map((i) => (i.id === id ? { ...i, ...cambi } : i)));

    const avvia = async (id) => {
        const t = compiti.current.get(id);
        if (!t) return;
        aggiorna(id, { stato: 'invio', progresso: 0, messaggio: null, tentativo: 1 });
        try {
            if (!t.pronto) t.pronto = await t.prepara();
        } catch (e) {
            aggiorna(id, { stato: 'errore', messaggio: e.message });
            return;
        }
        try {
            await conTentativi(() => t.manda(t.pronto, (p) => aggiorna(id, { progresso: p }), t.invioId),
                { sulTentativo: (n) => aggiorna(id, { tentativo: n, progresso: 0 }) });
            aggiorna(id, { stato: 'fatto', progresso: 100 });
            if (t.foto) annunciaFotoCambiate();
        } catch (e) {
            aggiorna(id, { stato: 'errore', messaggio: messaggioDa(e, 'Non riesco a caricare il file.') });
        }
    };

    /* I campi di testo PRIMA dei file: il server li legge nell'ordine.
       L'identificativo dell'invio e' lo stesso a ogni tentativo e a
       «Riprova» (sta nel compito): il server non archivia due volte. */
    const post = (percorso, fd, alProgresso, invioId) => api.post(percorso, fd, {
        headers: { 'Content-Type': undefined, ...(invioId ? { [INTESTAZIONE_INVIO]: invioId } : {}) },
        onUploadProgress: (ev) => { if (ev && ev.total) alProgresso(Math.round((ev.loaded * 100) / ev.total)); },
    });

    const parti = async (lista) => {
        /* Un invio nuovo, un identificativo nuovo: nasce qui, una volta per
           compito, e non a ogni tentativo. */
        lista.forEach((c) => { c.invioId = nuovoInvioId(); compiti.current.set(c.id, c); });
        setInvii(lista.map((c) => ({ id: c.id, titolo: c.titolo, stato: 'attesa', progresso: 0, tentativo: 0, messaggio: null })));
        elementi.forEach((e) => e.anteprima && URL.revokeObjectURL(e.anteprima));
        setElementi([]);
        setFase('invio');
        /* Uno alla volta: su una rete da telefono due invii insieme si
           rallentano a vicenda, e l'ordine resta quello degli scatti. */
        for (const c of lista) await avvia(c.id);
    };

    const nuovoId = () => { prossimo.current += 1; return `i${prossimo.current}`; };

    /* La visita dove vanno, se ci vanno: la visita aperta, o la riservata
       proposta finche' non si sceglie la galleria. */
    const visitaDest = dest.stato === 'fatto' && (dest.tipo === 'visita' || (dest.tipo === 'proposta' && !galleria))
        ? dest.visita : null;
    /* Dentro una visita, senza sapere se e' di oggi e del medico, non si
       carica: la galleria la vedrebbero tutti. Fuori, si carica come prima. */
    const destinazioneFerma = dest.stato === 'leggo' || (dest.stato === 'errore' && !!visitaAperta);

    const comeFotografie = () => {
        const et = etichetta;
        const vid = visitaDest ? visitaDest.id : null;
        const lista = elementi.map((e, i) => ({
            id: nuovoId(), titolo: `Fotografia ${i + 1}`, foto: true,
            prepara: async () => {
                /* Un'immagine che il browser non apre parte com'e': il server
                   la riconosce dai byte. Un HEIC no (utils/riduciImmagine.js,
                   «HEIC»): il server non lo legge, e nella galleria non si
                   vedrebbe dai computer; resta in rosso col motivo. */
                try { return { blob: await riduci(e.blob, LATO_FOTO), nome: comeJpeg(e.nome) }; } catch (x) {
                    if (await eHeic(e.blob)) throw new Error(MSG_HEIC_NON_APRIBILE);
                    return { blob: e.blob, nome: e.nome || 'foto' };
                }
            },
            manda: (p, alProgresso, invioId) => {
                const fd = new FormData();
                fd.append('etichetta', et);
                if (vid) fd.append('visita_id', String(vid));
                fd.append('file', p.blob, p.nome);
                return post(`/foto/paziente/${pazienteId}`, fd, alProgresso, invioId);
            },
        }));
        parti(lista);
    };

    const comeDocumento = () => {
        const cat = categoria;
        const descr = descrizione.trim();
        /* Nella visita: fra i documenti della visita (la rotta delle
           fotografie, «uso: documento», «archivio: documenti»: la riga nasce
           in `documenti`, non in paziente_foto), con la riservatezza della
           visita. Fuori: la rotta dei referti di sempre, con tipo e
           descrizione. */
        const vid = visitaDest ? visitaDest.id : null;
        const percorso = vid ? `/foto/paziente/${pazienteId}` : `/referti/paziente/${pazienteId}`;
        const immagini = elementi.filter((e) => !e.pdf);
        const pdf = elementi.filter((e) => e.pdf);
        const campi = (fd) => {
            if (vid) {
                fd.append('visita_id', String(vid));
                fd.append('uso', 'documento');
                fd.append('archivio', 'documenti');
                return;
            }
            fd.append('categoria', cat);
            if (descr) fd.append('descrizione', descr);
        };
        const lista = [];
        if (immagini.length) {
            const n = immagini.length;
            lista.push({
                id: nuovoId(), titolo: n === 1 ? 'Documento di 1 pagina' : `Documento di ${n} pagine`, foto: !!vid,
                prepara: async () => {
                    const pagine = [];
                    for (let i = 0; i < immagini.length; i += 1) {
                        try { pagine.push(await riduci(immagini[i].blob, LATO_DOCUMENTO)); } catch (x) {
                            if (await eHeic(immagini[i].blob)) throw new Error(`Pagina ${i + 1}: ${MSG_HEIC_NON_APRIBILE}`);
                            throw new Error(MSG_PAGINA_NON_APRIBILE(i + 1));
                        }
                    }
                    return pagine;
                },
                manda: (pagine, alProgresso, invioId) => {
                    const fd = new FormData();
                    campi(fd);
                    pagine.forEach((b, i) => fd.append('pagine', b, `pagina_${i + 1}.jpg`));
                    return post(percorso, fd, alProgresso, invioId);
                },
            });
        }
        pdf.forEach((e, i) => lista.push({
            id: nuovoId(), titolo: pdf.length === 1 ? 'PDF' : `PDF ${i + 1}`, foto: !!vid,
            prepara: async () => e.blob,
            manda: (blob, alProgresso, invioId) => {
                const fd = new FormData();
                campi(fd);
                fd.append('file', blob, e.nome || 'documento.pdf');
                return post(percorso, fd, alProgresso, invioId);
            },
        }));
        parti(lista);
    };

    const conPdf = elementi.some((e) => e.pdf);
    const nImmagini = elementi.filter((e) => !e.pdf).length;
    const inCorso = invii.some((i) => i.stato === 'invio' || i.stato === 'attesa');
    const tuttiFatti = invii.length > 0 && invii.every((i) => i.stato === 'fatto');

    const testoStato = (i) => {
        if (i.stato === 'invio') {
            const t = i.tentativo > 1 ? ` (tentativo ${i.tentativo} di ${TENTATIVI})` : '';
            return `invio ${i.progresso || 0}%${t}`;
        }
        return STATO_TESTO[i.stato] || '';
    };

    return (
        <>
            {barra !== 'nessuna' && (
                <>
                    <div className="tel-spazio" aria-hidden="true" />
                    <div className="tel-barra" data-carica-telefono="">
                        {barra === 'completa' && (
                            <button type="button" className="tel-barra-pulsante" onClick={apriFotocamera}>Scatta</button>
                        )}
                        <button type="button" className="tel-barra-pulsante"
                                onClick={() => caricaRef.current && caricaRef.current.click()}>Carica</button>
                    </div>
                </>
            )}
            <input ref={caricaRef} type="file" multiple accept={ACCETTA_CARICA} hidden
                   data-non-trattiene="" data-tel="carica" onChange={dalSelettore} />
            <input ref={ripiegoRef} type="file" accept={ACCETTA_SCATTO} capture="environment" hidden
                   data-non-trattiene="" data-tel="ripiego" onChange={dalSelettore} />

            {aperto && (
                <div className="tel-pannello" role="dialog" aria-modal="true" aria-label="Scatta o carica nella scheda">
                    <div className="tel-testa">
                        <h2 className="tel-titolo">
                            {fase === 'fotocamera' ? 'Scatta' : fase === 'invio' ? 'Invio' : 'Che cosa sono?'}
                            {perChi && <span className="tel-per-chi rm-tronca" data-per-chi="">{perChi}</span>}
                        </h2>
                        <button type="button" className="tel-chiudi" onClick={chiudi} disabled={inCorso}>Chiudi</button>
                    </div>

                    {fase === 'fotocamera' && (
                        <div className="tel-fotocamera">
                            {camera === 'avvio' && <p className="tel-nota">Apro la fotocamera…</p>}
                            {camera === 'non_disponibile' && (
                                <div className="tel-nota">
                                    <p>La fotocamera nella pagina non si apre: il permesso è negato, o il browser non la offre.</p>
                                    <button type="button" className="tel-pulsante"
                                            onClick={() => ripiegoRef.current && ripiegoRef.current.click()}>
                                        Usa la fotocamera del telefono
                                    </button>
                                </div>
                            )}
                            <video ref={videoRef} className="tel-video" playsInline muted autoPlay
                                   hidden={camera !== 'attiva'} />
                            <div className="tel-comandi">
                                <button type="button" className="tel-scatto" onClick={scatta}
                                        disabled={camera !== 'attiva'} aria-label="Scatta la foto" />
                                <button type="button" className="tel-pulsante" onClick={fineScatti}>
                                    {elementi.length ? `Fine (${elementi.length})` : 'Fine'}
                                </button>
                            </div>
                        </div>
                    )}

                    {elementi.length > 0 && (
                        <ol className="tel-miniature">
                            {elementi.map((e, i) => (
                                <li key={e.id} className="tel-miniatura">
                                    {e.anteprima
                                        ? <img src={e.anteprima} alt={`Scatto ${i + 1}`} />
                                        : <span className="tel-pdf">PDF</span>}
                                    <span className="tel-numero">{i + 1}</span>
                                    {i === elementi.length - 1 && (
                                        <button type="button" className="tel-togli" onClick={togliUltimo}>Togli</button>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}

                    {fase === 'elenco' && (
                        <div className="tel-scelta">
                            <div className="tel-riga">
                                <button type="button" className="tel-pulsante" onClick={apriFotocamera}>Altri scatti</button>
                                <button type="button" className="tel-pulsante"
                                        onClick={() => caricaRef.current && caricaRef.current.click()}>Aggiungi file</button>
                            </div>
                            {elementi.length === 0 ? (
                                <p className="tel-nota">Nessuno scatto e nessun file.</p>
                            ) : (
                                <>
                                    <Destinazione dest={dest} galleria={galleria} onGalleria={setGalleria}
                                                  dentroVisita={!!visitaAperta} onRiprova={() => setLettura((n) => n + 1)} />
                                    <fieldset className="tel-gruppo">
                                        <legend>Fotografie</legend>
                                        <label className="tel-campo">
                                            Etichetta
                                            <select value={etichetta} onChange={(ev) => setEtichetta(ev.target.value)}>
                                                {ETICHETTE.map((x) => <option key={x} value={x}>{x}</option>)}
                                            </select>
                                        </label>
                                        {conPdf && <p className="tel-nota">Fra i file c'è un PDF: si carica solo come documento.</p>}
                                        <button type="button" className="tel-pulsante tel-primario" disabled={conPdf || destinazioneFerma}
                                                onClick={comeFotografie}>Carica come fotografie</button>
                                    </fieldset>
                                    <fieldset className="tel-gruppo">
                                        <legend>Documento</legend>
                                        {visitaDest ? (
                                            <p className="tel-nota">Il documento va fra i documenti della visita.</p>
                                        ) : (
                                            <>
                                                <label className="tel-campo">
                                                    Tipo
                                                    <select value={categoria} onChange={(ev) => setCategoria(ev.target.value)}>
                                                        {CATEGORIE.map((c) => <option key={c.v} value={c.v}>{c.t}</option>)}
                                                    </select>
                                                </label>
                                                <label className="tel-campo">
                                                    Descrizione
                                                    <input type="text" value={descrizione}
                                                           onChange={(ev) => setDescrizione(ev.target.value)} />
                                                </label>
                                            </>
                                        )}
                                        {nImmagini > 1 && (
                                            <p className="tel-nota">
                                                {`Le ${nImmagini} fotografie diventano un documento di ${nImmagini} pagine, in quest'ordine.`}
                                            </p>
                                        )}
                                        <button type="button" className="tel-pulsante tel-primario" disabled={destinazioneFerma}
                                                onClick={comeDocumento}>Carica come documento</button>
                                    </fieldset>
                                </>
                            )}
                        </div>
                    )}

                    {fase === 'invio' && (
                        <div className="tel-invii">
                            <ul className="tel-elenco-invii">
                                {invii.map((i) => (
                                    <li key={i.id} className={`tel-invio tel-invio--${i.stato}`} data-stato={i.stato}>
                                        <span className="tel-invio-titolo">{i.titolo}</span>
                                        {i.stato === 'errore' ? (
                                            <>
                                                <span className="tel-invio-errore" role="alert">{i.messaggio}</span>
                                                <button type="button" className="tel-pulsante" disabled={inCorso}
                                                        onClick={() => avvia(i.id)}>Riprova</button>
                                            </>
                                        ) : (
                                            <span className="tel-invio-stato">{testoStato(i)}</span>
                                        )}
                                        {i.stato === 'invio' && (
                                            <progress className="tel-avanzamento" max="100" value={i.progresso || 0} />
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {tuttiFatti && <p className="tel-nota tel-ok">Tutto caricato nella scheda del paziente.</p>}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

/* Dove vanno: una riga con la visita, o la proposta della visita riservata
   con la galleria accanto e il suo avviso. Gli attributi per le prove:
   data-destinazione (leggo, errore, visita, proposta, galleria), data-dove. */
function Destinazione({ dest, galleria, onGalleria, dentroVisita, onRiprova }) {
    if (dest.stato === 'leggo') {
        return <p className="tel-nota" data-destinazione="leggo">Cerco la visita di oggi…</p>;
    }
    if (dest.stato === 'errore') {
        return (
            <div className="tel-gruppo" data-destinazione="errore">
                <p className="tel-nota" role="alert">
                    {MSG_VISITE_NON_LETTE}
                    {dentroVisita ? ' Riprova prima di caricare.' : ` ${MSG_AVVISO_GALLERIA}`}
                </p>
                <button type="button" className="tel-pulsante" onClick={onRiprova}>Riprova</button>
            </div>
        );
    }
    if (dest.tipo === 'visita') {
        return (
            <p className="tel-nota tel-ok" data-destinazione="visita">
                {dest.visita?.riservata ? MSG_NELLA_VISITA_RISERVATA : MSG_NELLA_VISITA}
            </p>
        );
    }
    if (dest.tipo === 'proposta') {
        return (
            <fieldset className="tel-gruppo" data-destinazione="proposta">
                <legend>Dove</legend>
                <div className="tel-riga">
                    <button type="button" className={`tel-pulsante${!galleria ? ' tel-primario' : ''}`}
                            data-dove="visita" aria-pressed={!galleria} onClick={() => onGalleria(false)}>
                        Nella visita riservata di oggi
                    </button>
                    <button type="button" className={`tel-pulsante${galleria ? ' tel-primario' : ''}`}
                            data-dove="galleria" aria-pressed={galleria} onClick={() => onGalleria(true)}>
                        Nella galleria
                    </button>
                </div>
                <p className="tel-nota" role={galleria ? 'alert' : undefined}>
                    {galleria ? MSG_AVVISO_GALLERIA : MSG_NELLA_VISITA_RISERVATA}
                </p>
            </fieldset>
        );
    }
    return null;
}

export default CaricaDalTelefono;
